import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spin } from 'antd';

import BasicLayout from '@/layouts/BasicLayout';
import PermissionsBootstrap from '@/components/auth/PermissionsBootstrap';
import RequirePerm from '@/components/auth/RequirePerm';
import { useAuthStore } from '@/stores/auth';
import { lazyPage } from '@/utils/lazyPage';
import { PAYMENT_ACCESS_READ } from '@/pages/payment/access/permissions';
import { OPEN_APP_READ } from '@/pages/open-platform/apps/permissions';

const StoryReviewPage = lazyPage(() => import('@/pages/ai/story-review'));
const LoginPage = lazyPage(() => import('@/pages/login'));
const DashboardPage = lazyPage(() => import('@/pages/dashboard'));
const DevicesPage = lazyPage(() => import('@/pages/devices'));
const UsersPage = lazyPage(() => import('@/pages/users'));
const UserDetailPage = lazyPage(() => import('@/pages/users/detail'));
const LlmConfigsPage = lazyPage(() => import('@/pages/llm/configs'));
const LlmModelsPage = lazyPage(() => import('@/pages/llm/models'));
const LlmRecommendedPage = lazyPage(() => import('@/pages/llm/recommended'));
const AiMeteringPage = lazyPage(() => import('@/pages/llm/metering'));
const AiPricesPage = lazyPage(() => import('@/pages/llm/prices'));
const ProfilesPage = lazyPage(() => import('@/pages/profiles'));
const CompaniesPage = lazyPage(() => import('@/pages/catalog/companies'));
const CompanyDetailPage = lazyPage(() => import('@/pages/catalog/companies/detail'));
const BrandDetailPage = lazyPage(() => import('@/pages/catalog/brands/detail'));
const ProductsPage = lazyPage(() => import('@/pages/catalog/products'));
const ProductDetailPage = lazyPage(() => import('@/pages/catalog/products/detail'));
const BrandAppDetailPage = lazyPage(() => import('@/pages/catalog/brand-apps/detail'));
const PlansPage = lazyPage(() => import('@/pages/plans'));
const SnDashboardPage = lazyPage(() => import('@/pages/sn/dashboard'));
const SnFactoriesPage = lazyPage(() => import('@/pages/sn/factories'));
const SnBatchesPage = lazyPage(() => import('@/pages/sn/batches'));
const SnBatchNewPage = lazyPage(() => import('@/pages/sn/batches/new'));
const SnBatchDetailPage = lazyPage(() => import('@/pages/sn/batches/detail'));
const SnListPage = lazyPage(() => import('@/pages/sn/list'));
const SnDetailPage = lazyPage(() => import('@/pages/sn/list/detail'));
const SnParserPage = lazyPage(() => import('@/pages/sn/parser'));
const DeviceDetailPage = lazyPage(() => import('@/pages/devices/detail'));
const DeviceCommandDetailPage = lazyPage(() => import('@/pages/devices/command-detail'));
const DeviceCrashDetailPage = lazyPage(() => import('@/pages/devices/crash-detail'));
const SubscriptionsPage = lazyPage(() => import('@/pages/subscriptions'));
const SubscriptionDetailPage = lazyPage(() => import('@/pages/subscriptions/detail'));
const SubscriptionGrantsPage = lazyPage(() => import('@/pages/subscriptions/grants'));
const FirmwarePage = lazyPage(() => import('@/pages/firmware'));
const FeedbackPage = lazyPage(() => import('@/pages/feedback'));
const DeviceCalendarPage = lazyPage(() => import('@/pages/device-calendar'));
const WatchFaceListPage = lazyPage(() => import('@/pages/watch-face/list'));
const WatchCustomFacePage = lazyPage(() => import('@/pages/watch-face/custom'));
const UserWatchFacePage = lazyPage(() => import('@/pages/watch-face/user'));
const RbacRolesPage = lazyPage(() => import('@/pages/rbac/roles'));
const RbacMenusPage = lazyPage(() => import('@/pages/rbac/menus'));
const RbacAdminUsersPage = lazyPage(() => import('@/pages/rbac/admin-users'));
const RbacMePage = lazyPage(() => import('@/pages/rbac/me'));
const AuditLogPage = lazyPage(() => import('@/pages/system/audit-log'));
// DMP 第一批
const DeviceGroupsPage = lazyPage(() => import('@/pages/devices/groups'));
const OtaTasksPage = lazyPage(() => import('@/pages/devices/ota-tasks'));
const OtaTaskDetailPage = lazyPage(() => import('@/pages/devices/ota-tasks/detail'));
const OtaBaselinesPage = lazyPage(() => import('@/pages/devices/ota-baselines'));
const AlertRulesPage = lazyPage(() => import('@/pages/ops/alert-rules'));
const AlertEventsPage = lazyPage(() => import('@/pages/ops/alert-events'));
const AlertChannelsPage = lazyPage(() => import('@/pages/ops/alert-channels'));
const RemoteConfigPage = lazyPage(() => import('@/pages/ops/config'));
const ErrorCodesPage = lazyPage(() => import('@/pages/system/error-codes'));
// DMP 第三批
const RepairTicketsPage = lazyPage(() => import('@/pages/repair/tickets'));
const RepairTicketDetailPage = lazyPage(() => import('@/pages/repair/tickets/detail'));
const EsimUsageListPage = lazyPage(() => import('@/pages/subscription/esim-usage'));
// 订阅二期 U24：AI 使用待决（查看与人工判失败分别授权）
const PartnersPage = lazyPage(() => import('@/pages/operation/partner'));
const PartnerDetailPage = lazyPage(() => import('@/pages/operation/partner/detail'));
const CapabilitiesPage = lazyPage(() => import('@/pages/catalog/capabilities'));
// DMP 第五批
const FitmentTargetPage = lazyPage(() => import('@/pages/catalog/fitment-target'));
const PreinstallAppPage = lazyPage(() => import('@/pages/catalog/preinstall-app'));
const PreinstallAppDetailPage = lazyPage(() => import('@/pages/catalog/preinstall-app/detail'));
const AppBundlePage = lazyPage(() => import('@/pages/catalog/app-bundle'));
const AppBundleDetailPage = lazyPage(() => import('@/pages/catalog/app-bundle/detail'));
const DeviceConfigListPage = lazyPage(() => import('@/pages/device/config'));
const DeviceConfigDetailPage = lazyPage(() => import('@/pages/device/config/detail'));
const ConfigDispatchListPage = lazyPage(() => import('@/pages/device/config-dispatch'));
const ConfigDispatchDetailPage = lazyPage(() => import('@/pages/device/config-dispatch/detail'));
const SlideshowGroupPage = lazyPage(() => import('@/pages/device/slideshow-group'));
const SnSchemeListPage = lazyPage(() => import('@/pages/sn/scheme'));
const SnSchemeDetailPage = lazyPage(() => import('@/pages/sn/scheme/detail'));
// 产线在线发号（模式 B）：产测报告 / SN 作废记录，两页共用 admin:factory-production:read
const FactoryTestReportPage = lazyPage(() => import('@/pages/sn/test-report'));
const SnScrapRecordPage = lazyPage(() => import('@/pages/sn/scrap-record'));
const RmaListPage = lazyPage(() => import('@/pages/repair/rma'));
const RmaDetailPage = lazyPage(() => import('@/pages/repair/rma/detail'));
// DMP 第四批
const FactoryAccountsPage = lazyPage(() => import('@/pages/system/factory-accounts'));
const PaymentAccessPage = lazyPage(() => import('@/pages/payment/access'));
// 开放平台（V153）：三方接入方的应用注册与密钥管理
const OpenAppListPage = lazyPage(() => import('@/pages/open-platform/apps'));
const OpenAppDetailPage = lazyPage(() => import('@/pages/open-platform/apps/detail'));
const DeviceEventsPage = lazyPage(() => import('@/pages/devices/events'));
const ConnectivityPage = lazyPage(() => import('@/pages/ops/connectivity'));
const ImageAssetPage = lazyPage(() => import('@/pages/ops/image-asset'));
const WorkbenchPage = lazyPage(() => import('@/pages/operation/workbench'));
const NotificationTemplatesPage = lazyPage(() => import('@/pages/operation/notifications/templates'));
const NotificationTemplateDetailPage = lazyPage(() => import('@/pages/operation/notifications/templates/detail'));
const NotificationDeliveriesPage = lazyPage(() => import('@/pages/operation/notifications/deliveries'));
const NotificationDeliveryDetailPage = lazyPage(() => import('@/pages/operation/notifications/deliveries/detail'));
// 墨水屏礼物模式（管理端只读）
const GiftsPage = lazyPage(() => import('@/pages/devices/gifts'));
const NotFoundPage = lazyPage(() => import('@/pages/NotFound'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  if (!token) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return (
    <>
      <PermissionsBootstrap />
      {children}
    </>
  );
}

const fallback = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin />
  </div>
);

/**
 * 单一来源地址：每条 admin route 进入门槛（"至少一个 :read 即可"）。
 * - 写操作的二级 gate 在页面内由 usePerm() 控制按钮显隐。
 * - calendar 没有 :read 权限码（V29 故意未发），暂用 anyOf(create, delete) 兜底；
 *   待后端补 GET 端点后改为 admin:calendar:read。
 * - sn/parser 是开发工具，归在 admin:sn:read 下。
 * - llm/recommended 跟 C 端共享，不走 RBAC，不加 gate。
 */
const gate = (anyOf: string[], element: React.ReactNode, options: { denyWhenDegraded?: boolean } = {}) => (
  <RequirePerm anyOf={anyOf} denyWhenDegraded={options.denyWhenDegraded}>{element}</RequirePerm>
);

export default function AppRouter() {
  return (
    <Suspense fallback={fallback}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <BasicLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="ai/story-review" element={gate(['admin:story-review:read'], <StoryReviewPage />)} />

          {/* 设备管理：path 对齐后端 menus 树（/device/*） */}
          <Route path="device/list" element={gate(['admin:device:read'], <DevicesPage />)} />
          <Route
            path="device/list/:id"
            element={gate(['admin:device:read'], <DeviceDetailPage />)}
          />
          <Route
            path="device/version"
            element={gate(['admin:device-version:read'], <FirmwarePage />)}
          />
          <Route
            path="device/calendar"
            element={gate(['admin:calendar:read'], <DeviceCalendarPage />)}
          />
          {/* DMP：设备分组 / OTA 任务 */}
          <Route
            path="device/groups"
            element={gate(['admin:group:read'], <DeviceGroupsPage />)}
          />
          <Route
            path="device/ota-tasks"
            element={gate(['admin:ota-task:read'], <OtaTasksPage />)}
          />
          <Route
            path="device/ota-tasks/:id"
            element={gate(['admin:ota-task:read'], <OtaTaskDetailPage />)}
          />
          <Route
            path="device/ota-baselines"
            element={gate(['admin:ota-baseline:read'], <OtaBaselinesPage />)}
          />
          <Route
            path="device/events"
            element={gate(['admin:event:search'], <DeviceEventsPage />)}
          />
          {/* DMP 第五批：系统配置项 + 配置下发任务 */}
          <Route
            path="device/config"
            element={gate(['admin:device-config:read'], <DeviceConfigListPage />)}
          />
          <Route
            path="device/config/:id"
            element={gate(['admin:device-config:read'], <DeviceConfigDetailPage />)}
          />
          <Route
            path="device/config-dispatch"
            element={gate(['admin:config-dispatch:read'], <ConfigDispatchListPage />)}
          />
          <Route
            path="device/config-dispatch/:id"
            element={gate(['admin:config-dispatch:read'], <ConfigDispatchDetailPage />)}
          />
          {/* 轮播图组：按 SN / 邮箱查组，看并下载设备端 BIN（V134）。
              下载与预览另由 admin:slideshow-group:download 在页内二级 gate */}
          <Route
            path="device/slideshow-group"
            element={gate(['admin:slideshow-group:read'], <SlideshowGroupPage />)}
          />
          {/* 墨水屏礼物：只读看板，礼物由送礼人侧 H5 创建 */}
          <Route path="device/gifts" element={gate(['admin:gift:read'], <GiftsPage />)} />
          {/* 命令 / 崩溃详情（可路由）：设备时间轴「查看关联」跳转目标 */}
          <Route
            path="device-command/:id"
            element={gate(['admin:device:command-read'], <DeviceCommandDetailPage />)}
          />
          <Route
            path="crash/:id"
            element={gate(['admin:crash:read'], <DeviceCrashDetailPage />)}
          />

          {/* 平台运营：/operation/* */}
          <Route path="operation/user" element={gate(['admin:user:read'], <UsersPage />)} />
          <Route
            path="operation/user/:id"
            element={gate(['admin:user:read'], <UserDetailPage />)}
          />
          <Route
            path="operation/feedback"
            element={gate(['admin:feedback:read'], <FeedbackPage />)}
          />
          <Route
            path="operation/partner"
            element={gate(['admin:partner:read'], <PartnersPage />)}
          />
          <Route
            path="operation/partner/:id"
            element={gate(['admin:partner:read'], <PartnerDetailPage />)}
          />
          <Route
            path="operation/workbench"
            element={gate(['admin:device:read'], <WorkbenchPage />)}
          />
          <Route
            path="operation/notifications"
            element={<Navigate to="/operation/notifications/templates" replace />}
          />
          <Route
            path="operation/notifications/templates"
            element={gate(['admin:notification-template:read'], <NotificationTemplatesPage />)}
          />
          <Route
            path="operation/notifications/templates/:templateId"
            element={gate(['admin:notification-template:read'], <NotificationTemplateDetailPage />)}
          />
          <Route
            path="operation/notifications/deliveries"
            element={gate(['admin:notification-delivery:read'], <NotificationDeliveriesPage />)}
          />
          <Route
            path="operation/notifications/deliveries/:requestId"
            element={gate(['admin:notification-delivery:read'], <NotificationDeliveryDetailPage />)}
          />

          {/* 订阅管理：/subscription/* */}
          <Route
            path="subscription/plan"
            element={gate(['admin:plan:read'], <PlansPage />)}
          />
          <Route
            path="subscription/device-plan"
            element={gate(['admin:device-plan:read'], <SubscriptionsPage />)}
          />
          <Route
            path="subscription/device-plan/grants"
            element={gate(['admin:subscription-grant:read'], <SubscriptionGrantsPage />, { denyWhenDegraded: true })}
          />
          <Route
            path="subscription/device-plan/:id"
            element={gate(['admin:device-plan:read'], <SubscriptionDetailPage />)}
          />
          <Route
            path="subscription/esim-usage"
            element={gate(['admin:esim:read'], <EsimUsageListPage />)}
          />
          <Route path="watch-face">
            <Route index element={<Navigate to="/watch-face/list" replace />} />
            <Route
              path="list"
              element={gate(['admin:watch-face:read'], <WatchFaceListPage />)}
            />
            <Route
              path="custom"
              element={gate(['admin:watch-custom-face:read'], <WatchCustomFacePage />)}
            />
            <Route
              path="user"
              element={gate(['admin:user-watch-face:read'], <UserWatchFacePage />)}
            />
          </Route>

          <Route path="llm">
            <Route index element={<Navigate to="/llm/configs" replace />} />
            <Route
              path="configs"
              element={gate(['admin:llm-config:read'], <LlmConfigsPage />)}
            />
            <Route
              path="models"
              element={gate(['admin:llm-model:read'], <LlmModelsPage />)}
            />
            {/* 新计量页与旧「使用统计」并存：并行期要用旧口径给新口径做基线。
                旧页的退役条件见实施方案 Task 16，不在这里顺手删。 */}
            <Route
              path="metering"
              element={gate(['admin:ai-metering:read'], <AiMeteringPage />)}
            />
            <Route
              path="prices"
              element={gate(['admin:ai-pricing:read'], <AiPricesPage />)}
            />
            {/* 对账已并入价格管理的页签；旧路径保留重定向，收藏链接不失效 */}
            <Route
              path="price-audit"
              element={<Navigate to="/llm/prices?tab=audit" replace />}
            />
            <Route
              path="recommend"
              element={gate(['admin:llm-recommend:read'], <LlmRecommendedPage />)}
            />
            {/* Profile 管理在后端树里挂在大模型目录下：/llm/profile */}
            <Route
              path="profile"
              element={gate(['admin:profile:read'], <ProfilesPage />)}
            />
          </Route>

          {/* RBAC 个人资料：所有登录用户可进，经头像下拉进入（不在侧边栏） */}
          <Route path="rbac">
            <Route index element={<Navigate to="/rbac/me" replace />} />
            <Route path="me" element={<RbacMePage />} />
          </Route>

          {/* 系统管理：仅 SUPER_ADMIN。path 对齐后端 menus 树（/system/*）；
              导出按钮另由 admin:audit:export 二级 gate 控制 */}
          <Route path="system">
            <Route
              path="admin-user"
              element={
                <RequirePerm superAdminOnly>
                  <RbacAdminUsersPage />
                </RequirePerm>
              }
            />
            <Route
              path="role"
              element={
                <RequirePerm superAdminOnly>
                  <RbacRolesPage />
                </RequirePerm>
              }
            />
            <Route
              path="menu"
              element={
                <RequirePerm superAdminOnly>
                  <RbacMenusPage />
                </RequirePerm>
              }
            />
            <Route
              path="audit-log"
              element={
                <RequirePerm superAdminOnly>
                  <AuditLogPage />
                </RequirePerm>
              }
            />
            {/* 错误码字典：客服也需查阅，用 admin:error-code:read 而非 superAdminOnly */}
            <Route
              path="error-codes"
              element={gate(['admin:error-code:read'], <ErrorCodesPage />)}
            />
            <Route
              path="factory-accounts"
              element={gate(['admin:factory-account:read'], <FactoryAccountsPage />)}
            />
            {/* 兼容历史收藏地址；实际页面与权限校验统一收口到 /payment/access。 */}
            <Route
              path="payment-access"
              element={<Navigate to="/payment/access" replace />}
            />
          </Route>

          {/* 支付管理：读取与三类写操作由后端 RBAC 分别授权。 */}
          <Route path="payment">
            <Route
              path="access"
              element={
                <RequirePerm permission={PAYMENT_ACCESS_READ}>
                  <PaymentAccessPage />
                </RequirePerm>
              }
            />
          </Route>

          {/* 开放平台：三方接入方的应用与密钥。写权限不含读权限，页内按 create /
              update / secret 三个码分别 gate（secret 独立，不与编辑合并）。 */}
          <Route path="open-platform">
            <Route index element={<Navigate to="/open-platform/apps" replace />} />
            <Route
              path="apps"
              element={
                <RequirePerm permission={OPEN_APP_READ}>
                  <OpenAppListPage />
                </RequirePerm>
              }
            />
            <Route
              path="apps/:id"
              element={
                <RequirePerm permission={OPEN_APP_READ}>
                  <OpenAppDetailPage />
                </RequirePerm>
              }
            />
          </Route>

          {/* 运维中心：/ops/*（告警 + Remote Config） */}
          <Route path="ops">
            <Route index element={<Navigate to="/ops/alert-rules" replace />} />
            <Route
              path="alert-rules"
              element={gate(['admin:alert:read'], <AlertRulesPage />)}
            />
            <Route
              path="alert-events"
              element={gate(['admin:alert:read'], <AlertEventsPage />)}
            />
            <Route
              path="alert-channels"
              element={gate(['admin:alert:read', 'admin:alert:channel:write'], <AlertChannelsPage />)}
            />
            <Route path="config" element={gate(['admin:config:read'], <RemoteConfigPage />)} />
            <Route
              path="connectivity"
              element={gate(['admin:device:connectivity'], <ConnectivityPage />)}
            />
            {/* 图片素材库：后端 V160 把菜单挂在「平台运营」目录下，但 path 定成 /ops/image-asset，
                侧边栏按后端树渲染所以入口仍在平台运营里；路由必须跟后端 path 走。
                上传 / 编辑 / 删除由 create / update / delete 在页内二级 gate */}
            <Route
              path="image-asset"
              element={gate(['admin:image-asset:read'], <ImageAssetPage />)}
            />
          </Route>

          <Route path="sn">
            <Route index element={<Navigate to="/sn/overview" replace />} />
            <Route
              path="overview"
              element={gate(['admin:sn-overview:read'], <SnDashboardPage />)}
            />
            <Route path="factory" element={gate(['admin:factory:read'], <SnFactoriesPage />)} />
            <Route path="batch" element={gate(['admin:sn-batch:read'], <SnBatchesPage />)} />
            <Route
              path="batch/new"
              element={gate(['admin:sn-batch:create'], <SnBatchNewPage />)}
            />
            <Route
              path="batch/:id"
              element={gate(['admin:sn-batch:read'], <SnBatchDetailPage />)}
            />
            <Route path="list" element={gate(['admin:sn:read'], <SnListPage />)} />
            <Route path="list/:id" element={gate(['admin:sn:read'], <SnDetailPage />)} />
            {/* 产线数据（只读）。作废记录那行菜单的 permission_code 是空的（V139 故意），
                但 controller 是类级 admin:factory-production:read，两条路由用同一个码守 */}
            <Route
              path="test-report"
              element={gate(['admin:factory-production:read'], <FactoryTestReportPage />)}
            />
            <Route
              path="scrap-record"
              element={gate(['admin:factory-production:read'], <SnScrapRecordPage />)}
            />
            <Route path="parser" element={gate(['admin:sn-parser:read'], <SnParserPage />)} />
            <Route
              path="scheme"
              element={gate(['admin:sn-scheme:read'], <SnSchemeListPage />)}
            />
            <Route
              path="scheme/:id"
              element={gate(['admin:sn-scheme:read'], <SnSchemeDetailPage />)}
            />
          </Route>

          <Route path="catalog">
            <Route index element={<Navigate to="/catalog/company" replace />} />
            <Route
              path="company"
              element={gate(['admin:company:read'], <CompaniesPage />)}
            />
            <Route
              path="company/:id"
              element={gate(['admin:company:read'], <CompanyDetailPage />)}
            />
            <Route path="brands/:id" element={gate(['admin:brand:read'], <BrandDetailPage />)} />
            <Route
              path="product-hub"
              element={gate(['admin:product:read'], <ProductsPage />)}
            />
            <Route
              path="product-hub/:id"
              element={gate(['admin:product:read'], <ProductDetailPage />)}
            />
            <Route
              path="brand-apps/:id"
              element={gate(['admin:brand-app:read'], <BrandAppDetailPage />)}
            />
            <Route
              path="capabilities"
              element={gate(['admin:capability:write', 'admin:product:read'], <CapabilitiesPage />)}
            />
            <Route
              path="fitment-target"
              element={gate(['admin:fitment-target:read'], <FitmentTargetPage />)}
            />
            {/* 预装应用字典（平台级、不挂品牌）与预装应用组，两个独立菜单 */}
            <Route
              path="preinstall-app"
              element={gate(['admin:preinstall-app:read'], <PreinstallAppPage />)}
            />
            {/* 详情 = 包版本管理面（上传 APK / 发布 / 归档），门槛仍是 :read，
                写操作按 package-upload / package-release 在页内 gate */}
            <Route
              path="preinstall-app/:id"
              element={gate(['admin:preinstall-app:read'], <PreinstallAppDetailPage />)}
            />
            <Route
              path="app-bundle"
              element={gate(['admin:app-bundle:read'], <AppBundlePage />)}
            />
            <Route
              path="app-bundle/:id"
              element={gate(['admin:app-bundle:read'], <AppBundleDetailPage />)}
            />
          </Route>

          {/* 售后服务：/repair/* */}
          <Route path="repair">
            <Route index element={<Navigate to="/repair/tickets" replace />} />
            <Route
              path="tickets"
              element={gate(['admin:repair:read'], <RepairTicketsPage />)}
            />
            <Route
              path="tickets/:id"
              element={gate(['admin:repair:read'], <RepairTicketDetailPage />)}
            />
            {/* 批次 RMA：后端 menus 树里挂在售后服务组 (id=301) 下、sort 872，
                排在售后工单 (871) 之后，不新开一级目录 */}
            <Route path="rma" element={gate(['admin:rma:read'], <RmaListPage />)} />
            <Route path="rma/:id" element={gate(['admin:rma:read'], <RmaDetailPage />)} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
